import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import { alpha } from '@mui/material/styles'
import {
  PERMISSIONS,
  addDays,
  formatCalendarDate,
  parseCalendarDate,
  weekDays,
  weekStartOf,
} from '@jupi/shared'
import { ApiError, IconButton, RemoteContent, Title, formatTime, useAuth, useSnackbar } from '@jupi/ui'
import BookAircraftDialog from '../features/firePatrol/BookAircraftDialog'
import ReleaseBookingDialog from '../features/firePatrol/ReleaseBookingDialog'
import ShiftDialog from '../features/firePatrol/ShiftDialog'
import { clubDay, isoWeek } from '../features/firePatrol/dates'
import {
  useFirePatrolCalendar,
  useFirePatrolCrew,
  useSetWeekDispatcher,
  type FirePatrolBooking,
  type FirePatrolShift,
} from '../features/firePatrol/api'

type View = 'week' | 'month'

const DISPATCH_COLOUR = '#e8a07c'

// The palolento (fire-patrol) calendar: a week (or month) of days, each with
// the aircraft booked for it and the people signed up -- the chosen ones
// marked -- under the dispatcher in charge of the week. The aircraft
// bookings are the main calendar's own rows.
export default function FirePatrolPage() {
  const { t, i18n } = useTranslation()
  const { can } = useAuth()
  const dispatcher = can(PERMISSIONS.firePatrolDispatch)
  const [params, setParams] = useSearchParams()

  const view: View = params.get('view') === 'month' ? 'month' : 'week'
  const anchor = parseCalendarDate(params.get('date')) ?? new Date()
  const anchorDay = formatCalendarDate(anchor)
  const weekStart = weekStartOf(anchorDay)

  // The grid's days: one week, or the whole weeks that cover the month.
  const days = useMemo(() => {
    if (view === 'week') return weekDays(weekStart)
    const first = `${anchorDay.slice(0, 7)}-01`
    const lastDate = new Date(Number(anchorDay.slice(0, 4)), Number(anchorDay.slice(5, 7)), 0)
    const gridStart = weekStartOf(first)
    const gridEnd = addDays(weekStartOf(formatCalendarDate(lastDate)), 6)
    const out: string[] = []
    for (let d = gridStart; d <= gridEnd; d = addDays(d, 1)) out.push(d)
    return out
  }, [view, weekStart, anchorDay])
  const from = days[0] as string
  const to = days[days.length - 1] as string

  const { data, isLoading, error } = useFirePatrolCalendar(from, to)

  const go = (next: { date?: string; view?: View }) => {
    const merged = new URLSearchParams(params)
    merged.set('date', next.date ?? anchorDay)
    merged.set('view', next.view ?? view)
    setParams(merged, { replace: true })
  }
  const step = (direction: 1 | -1) => {
    if (view === 'week') return go({ date: addDays(anchorDay, 7 * direction) })
    const target = new Date(anchor.getFullYear(), anchor.getMonth() + direction, 1, 12)
    go({ date: formatCalendarDate(target) })
  }

  const [dialog, setDialog] = useState<{ date: string; shift: FirePatrolShift | null } | null>(null)
  const [booking, setBooking] = useState(false)
  const [releasing, setReleasing] = useState<FirePatrolBooking | null>(null)
  // A dispatcher may release a patrol booking; one that is over is history,
  // which only an administrator amends.
  const canRelease = (b: FirePatrolBooking) =>
    dispatcher && (new Date(b.endsAt).getTime() > Date.now() || can(PERMISSIONS.bookingManageAny))

  // Day first, never the US month-first order: 12–18/10/2026.
  const weekdayOf = (day: string) =>
    new Intl.DateTimeFormat(i18n.language, { weekday: 'short' }).format(parseCalendarDate(day) ?? new Date())
  const dayLabel = (day: string) => `${weekdayOf(day)} ${dayMonth(day)}`

  const weekTitle = () => {
    const sameYear = from.slice(0, 4) === to.slice(0, 4)
    const sameMonth = sameYear && from.slice(5, 7) === to.slice(5, 7)
    const start = sameMonth ? String(Number(from.slice(8))) : sameYear ? dayMonth(from) : `${dayMonth(from)}/${from.slice(0, 4)}`
    return `${start}–${dayMonth(to)}/${to.slice(0, 4)}`
  }

  const title =
    view === 'week'
      ? weekTitle()
      : new Intl.DateTimeFormat(i18n.language, { month: 'long', year: 'numeric' }).format(anchor)

  const shiftsByDay = new Map<string, FirePatrolShift[]>()
  for (const shift of data?.shifts ?? []) {
    const list = shiftsByDay.get(shift.shiftDate) ?? []
    list.push(shift)
    shiftsByDay.set(shift.shiftDate, list)
  }
  const bookingsByDay = new Map<string, FirePatrolBooking[]>()
  for (const b of data?.bookings ?? []) {
    const day = clubDay(b.startsAt)
    const list = bookingsByDay.get(day) ?? []
    list.push(b)
    bookingsByDay.set(day, list)
  }

  return (
    <Box>
      <Title label={t('firePatrol.title')} />
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
          <IconButton aria-label={t('firePatrol.prev')} onClick={() => step(-1)}>
            <Icon icon="mdi:chevron-left" />
          </IconButton>
          <Button size="small" variant="outlined" onClick={() => go({ date: formatCalendarDate(new Date()) })}>
            {t('firePatrol.today')}
          </Button>
          <IconButton aria-label={t('firePatrol.next')} onClick={() => step(1)}>
            <Icon icon="mdi:chevron-right" />
          </IconButton>
          <Typography variant="h6" sx={{ flex: 1, minWidth: 180, textTransform: 'capitalize' }}>
            {title}
          </Typography>
          <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v: View | null) => v && go({ view: v })}>
            <ToggleButton value="week">{t('firePatrol.viewWeek')}</ToggleButton>
            <ToggleButton value="month">{t('firePatrol.viewMonth')}</ToggleButton>
          </ToggleButtonGroup>
          {dispatcher ? (
            <Button variant="contained" startIcon={<Icon icon="mdi:airplane-clock" />} onClick={() => setBooking(true)}>
              {t('firePatrol.bookAircraft')}
            </Button>
          ) : null}
        </Stack>

        <RemoteContent isLoading={isLoading} error={error}>
          {view === 'week' ? (
            <>
              <DispatcherBar
                weekStart={weekStart}
                current={data?.weeks.find((w) => w.weekStart === weekStart)}
                editable={dispatcher}
              />
              <Box
                sx={{
                  display: 'grid',
                  gap: 1,
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(7, minmax(0, 1fr))' },
                }}
              >
                {days.map((day) => (
                  <DayCard
                    key={day}
                    day={day}
                    weekday={weekdayOf(day)}
                    label={dayMonth(day)}
                    shifts={shiftsByDay.get(day) ?? []}
                    bookings={bookingsByDay.get(day) ?? []}
                    today={day === formatCalendarDate(new Date())}
                    onSignUp={() => setDialog({ date: day, shift: null })}
                    onOpen={(shift) => setDialog({ date: day, shift })}
                    onRelease={(b) => (canRelease(b) ? setReleasing(b) : undefined)}
                    canRelease={canRelease}
                  />
                ))}
              </Box>
            </>
          ) : (
            <MonthGrid
              days={days}
              month={anchorDay.slice(0, 7)}
              weeks={data?.weeks ?? []}
              shiftsByDay={shiftsByDay}
              bookingsByDay={bookingsByDay}
              dayLabel={(d) => dayLabel(d)}
              onPick={(day) => go({ date: day, view: 'week' })}
            />
          )}
        </RemoteContent>
        <Typography variant="caption" color="text.secondary">
          {t('firePatrol.visibleToCrew')}
        </Typography>
      </Stack>

      {dialog ? <ShiftDialog date={dialog.date} shift={dialog.shift} onClose={() => setDialog(null)} /> : null}
      {booking ? <BookAircraftDialog defaultDate={weekStart} onClose={() => setBooking(false)} /> : null}
      {releasing ? <ReleaseBookingDialog booking={releasing} onClose={() => setReleasing(null)} /> : null}
    </Box>
  )
}

// The week's dispatcher, as the orange bar across the top of the week.
function DispatcherBar({
  weekStart,
  current,
  editable,
}: {
  weekStart: string
  current: { dispatcherUserId: string; dispatcherName: string } | undefined
  editable: boolean
}) {
  const { t } = useTranslation()
  const { showSnackbar } = useSnackbar()
  const { data: crew = [] } = useFirePatrolCrew(editable)
  const setDispatcher = useSetWeekDispatcher()
  const [editing, setEditing] = useState(false)

  const choose = async (dispatcherUserId: string | null) => {
    try {
      await setDispatcher.mutateAsync({ weekStart, dispatcherUserId })
      setEditing(false)
    } catch (err) {
      showSnackbar(err instanceof ApiError ? err.message : t('common.error'), { severity: 'error' })
    }
  }

  return (
    <Paper
      variant="outlined"
      sx={{ px: 1.5, py: 0.75, bgcolor: alpha(DISPATCH_COLOUR, 0.35), borderColor: DISPATCH_COLOUR }}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minHeight: 36 }}>
        <Icon icon="mdi:headset" />
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {t('firePatrol.dispatcher')}:
        </Typography>
        {editing ? (
          <TextField
            select
            size="small"
            value={current?.dispatcherUserId ?? ''}
            onChange={(e) => choose(e.target.value || null)}
            sx={{ minWidth: 220 }}
            slotProps={{ select: { displayEmpty: true } }}
          >
            <MenuItem value="">{t('firePatrol.noOne')}</MenuItem>
            {crew
              .filter((c) => c.canDispatch)
              .map((c) => (
                <MenuItem key={c.userId} value={c.userId}>
                  {c.displayName}
                </MenuItem>
              ))}
          </TextField>
        ) : (
          <Typography variant="body2" sx={{ flex: 1 }}>
            {current?.dispatcherName ?? t('firePatrol.noDispatcher')}
          </Typography>
        )}
        {editable ? (
          <Button size="small" onClick={() => setEditing((v) => !v)} sx={{ ml: 'auto' }}>
            {editing ? t('firePatrol.close') : t('firePatrol.setDispatcher')}
          </Button>
        ) : null}
      </Stack>
    </Paper>
  )
}

function ShiftChip({ shift, onClick }: { shift: FirePatrolShift; onClick: () => void }) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const chosen = Boolean(shift.lockedAt)
  const colour = shift.role === 'pic' ? 'info' : shift.role === 'mc' ? 'success' : 'default'
  return (
    <Chip
      size="small"
      color={colour}
      variant={chosen ? 'filled' : 'outlined'}
      onClick={onClick}
      icon={chosen ? <Icon icon="mdi:lock" /> : undefined}
      title={shift.note ?? undefined}
      label={
        <Box component="span" sx={{ fontWeight: shift.userId === user?.id ? 700 : 400 }}>
          {shift.displayName}
          {shift.role ? ` · ${t(shift.role === 'pic' ? 'firePatrol.picShort' : 'firePatrol.mcShort')}` : ''}
          {shift.note ? ' …' : ''}
        </Box>
      }
      sx={{
        maxWidth: '100%',
        height: 'auto',
        minHeight: 24,
        justifyContent: 'flex-start',
        '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 },
      }}
    />
  )
}

function BookingBlock({ booking, onClick }: { booking: FirePatrolBooking; onClick?: () => void }) {
  const { t } = useTranslation()
  const option = booking.kind === 'option'
  return (
    <Paper
      variant="outlined"
      // A dispatcher clicks it to release it.
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => (e.key === 'Enter' || e.key === ' ') && onClick() : undefined}
      sx={{
        cursor: onClick ? 'pointer' : undefined,
        '&:hover': onClick ? { borderColor: 'primary.main' } : undefined,
        px: 1,
        py: 0.5,
        bgcolor: (theme) => alpha(theme.palette.primary.main, option ? 0.05 : 0.15),
        borderStyle: option ? 'dashed' : 'solid',
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
        <Icon icon="mdi:airplane" style={{ verticalAlign: -2, marginRight: 4 }} />
        {booking.registration}
        {option ? ` (${t('firePatrol.option')})` : ''}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {formatTime(booking.startsAt)}–{formatTime(booking.endsAt)}
      </Typography>
    </Paper>
  )
}

function DayCard({
  day,
  weekday,
  label,
  shifts,
  bookings,
  today,
  onSignUp,
  onOpen,
  onRelease,
  canRelease,
}: {
  day: string
  weekday: string
  label: string
  shifts: FirePatrolShift[]
  bookings: FirePatrolBooking[]
  today: boolean
  onSignUp: () => void
  onOpen: (shift: FirePatrolShift) => void
  onRelease: (booking: FirePatrolBooking) => void
  canRelease: (booking: FirePatrolBooking) => boolean
}) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const signedUp = shifts.some((s) => s.userId === user?.id)
  // Chosen first, then by role, so the day reads PIC / MC / the rest.
  // eslint-disable-next-line unicorn/no-array-sort -- sorts a copy
  const ordered = [...shifts].sort(
    (a, b) =>
      Number(Boolean(b.lockedAt)) - Number(Boolean(a.lockedAt)) ||
      (a.role ?? 'z').localeCompare(b.role ?? 'z') ||
      a.displayName.localeCompare(b.displayName),
  )
  return (
    <Paper variant="outlined" sx={{ p: 1, borderColor: today ? 'primary.main' : undefined, borderWidth: today ? 2 : 1 }}>
      <Stack spacing={0.75} data-day={day}>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2, textTransform: 'capitalize' }}>
            {weekday}
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {label}
          </Typography>
        </Box>
        {bookings.map((b) => (
          <BookingBlock key={b.bookingId} booking={b} onClick={canRelease(b) ? () => onRelease(b) : undefined} />
        ))}
        {ordered.map((s) => (
          <ShiftChip key={s.shiftId} shift={s} onClick={() => onOpen(s)} />
        ))}
        {ordered.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            {t('firePatrol.nobody')}
          </Typography>
        ) : null}
        {signedUp ? null : (
          <Button size="small" startIcon={<Icon icon="mdi:plus" />} onClick={onSignUp}>
            {t('firePatrol.signUp')}
          </Button>
        )}
      </Stack>
    </Paper>
  )
}

// The month at a glance: who is chosen, whether the aircraft is booked.
// Opening a day goes to its week.
function MonthGrid({
  days,
  month,
  weeks,
  shiftsByDay,
  bookingsByDay,
  dayLabel,
  onPick,
}: {
  days: string[]
  month: string
  weeks: { weekStart: string; dispatcherName: string }[]
  shiftsByDay: Map<string, FirePatrolShift[]>
  bookingsByDay: Map<string, FirePatrolBooking[]>
  dayLabel: (day: string) => string
  onPick: (day: string) => void
}) {
  const { t, i18n } = useTranslation()
  const headings = days.slice(0, 7).map((d) =>
    new Intl.DateTimeFormat(i18n.language, { weekday: 'short' }).format(parseCalendarDate(d) ?? new Date()),
  )
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box sx={{ display: 'grid', gap: 0.5, gridTemplateColumns: 'repeat(7, minmax(88px, 1fr))', minWidth: 640 }}>
        {headings.map((h) => (
          <Typography key={h} variant="caption" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>
            {h}
          </Typography>
        ))}
        {days.map((day) => {
          const shifts = shiftsByDay.get(day) ?? []
          const chosen = (role: 'pic' | 'mc') => shifts.some((s) => s.role === role && s.lockedAt)
          const booked = (bookingsByDay.get(day) ?? []).length > 0
          const inMonth = day.startsWith(month)
          const dispatcher = day === weekStartOf(day) ? weeks.find((w) => w.weekStart === day)?.dispatcherName : null
          return (
            <Paper
              key={day}
              variant="outlined"
              onClick={() => onPick(day)}
              sx={{ p: 0.75, minHeight: 84, cursor: 'pointer', opacity: inMonth ? 1 : 0.45, '&:hover': { bgcolor: 'action.hover' } }}
            >
              <Stack spacing={0.5}>
                <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    {Number(day.slice(8))}.
                    {isoWeekStart(day) ? ` · ${t('firePatrol.weekOf', { week: isoWeek(day) })}` : ''}
                  </Typography>
                  {booked ? <Icon icon="mdi:airplane" width={14} /> : null}
                </Stack>
                <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                  {shifts.length > 0 ? (
                    <>
                      <Chip size="small" label={t('firePatrol.picShort')} color={chosen('pic') ? 'info' : 'default'} variant={chosen('pic') ? 'filled' : 'outlined'} />
                      <Chip size="small" label={t('firePatrol.mcShort')} color={chosen('mc') ? 'success' : 'default'} variant={chosen('mc') ? 'filled' : 'outlined'} />
                    </>
                  ) : null}
                </Stack>
                {shifts.length > 0 ? (
                  <Typography variant="caption" color="text.secondary" aria-label={dayLabel(day)}>
                    {shifts.length}
                  </Typography>
                ) : null}
                {dispatcher ? (
                  <Typography variant="caption" noWrap sx={{ color: DISPATCH_COLOUR }}>
                    <Icon icon="mdi:headset" width={12} style={{ verticalAlign: -2 }} /> {dispatcher}
                  </Typography>
                ) : null}
              </Stack>
            </Paper>
          )
        })}
      </Box>
    </Box>
  )
}

// 14/10: day first, never the US month-first order.
const dayMonth = (day: string) => `${Number(day.slice(8))}/${Number(day.slice(5, 7))}`

const isoWeekStart = (day: string) => weekStartOf(day) === day
